import AppBaseStore from './baseStore'
import { useDjango } from '@/composables/useDjango'
import type { BaseStoreActions, BaseStoreType } from '@/types/storeTypes'
import { SectionLine, SectionLineUploadCollection } from '@/stores/type/sectionLine.type'
import API from '@/api/apiUrls'

const { api } = useDjango()

type State = BaseStoreType<SectionLineUploadCollection>

const baseUrl = API.SECTION_GEO_DATA.SECTION_LINE_UPLOAD_COLLECTIONS.INDEX
const baseStore: BaseStoreType<SectionLineUploadCollection> = new AppBaseStore(baseUrl, api)
const baseStoreActions: BaseStoreActions<SectionLineUploadCollection> = baseStore.actions

export const useSectionLineUploadCollectionsStore = defineStore({
  id: 'sectionLineUploadCollections',
  state: (): State => ({
    ...baseStore.state,
    loading: false,
    filterFields: ['name'],
    addSectionLineShapeFileDialog: {
      show: false,
      selectedMode: 'Upload'
    },
    importSectionLineFromSpeckleDialog: false,
    exportSectionLineToSpeckleDialog: false,
    selectedItems: []
  }),
  actions: {
    ...baseStoreActions,
    resetAll() {
      this.$reset()
    },
    addSelectedSectionLineCollection(sectionLineCollection: SectionLineUploadCollection) {
      if (this.selectedItems.includes(sectionLineCollection)) return
      this.selectedItems.push(sectionLineCollection)
    },
    removeSelectedSectionLineCollection(sectionLineCollection: SectionLineUploadCollection) {
      const index = this.selectedItems.findIndex((item) => item.id === sectionLineCollection.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      }
    },
    selectAllSectionLineCollections() {
      this.selectedItems = [...this.items]
      const sectionLinesStore = useSectionLinesStore()
      this.items.forEach((collection: SectionLineUploadCollection) => {
        sectionLinesStore.selectSectionLinesByCollectionId(collection.id)
      })
    },
    clearSelectedSectionLineCollections() {
      this.selectedItems = []
      const sectionLinesStore = useSectionLinesStore()
      this.items.forEach((collection: SectionLineUploadCollection) => {
        sectionLinesStore.deselectSectionLinesByCollectionId(collection.id)
      })
    },
    addHiddenFieldToItems() {
      this.items.forEach((item) => {
        item.hidden = false
      })
    },
    toggleItem(item: SectionLineUploadCollection) {
      const obj = this.items.find((obj) => obj.id === item.id)
      obj.hidden = !obj.hidden
    },
    toggleAddSectionLineShapeFileDialog(selectedMode?: 'Upload' | 'Create') {
      this.addSectionLineShapeFileDialog.show = !this.addSectionLineShapeFileDialog.show
      this.addSectionLineShapeFileDialog.selectedMode = selectedMode
    },
    toggleImportSectionLineFromSpeckleDialog(value: boolean | undefined) {
      if (typeof value === 'undefined') {
        this.importSectionLineFromSpeckleDialog = !this.importSectionLineFromSpeckleDialog
        return
      }
      this.importSectionLineFromSpeckleDialog = !!value
    },
    toggleExportSectionLineToSpeckleDialog(value: boolean | undefined) {
      if (typeof value === 'undefined') {
        this.exportSectionLineToSpeckleDialog = !this.exportSectionLineToSpeckleDialog
        return
      }
      this.exportSectionLineToSpeckleDialog = !!value
    },
    setCurrentItemById(id: number) {
      const item = this.items.find((item) => item.id === id)
      if (!item) return
      this.currentItem = item
    },
    removeSectionLineFromCollection(item: SectionLine) {
      this.items.forEach((collection) => {
        const sectionLineIndex = collection.sectionline_set.features.findIndex(
          (sectionLine) => sectionLine.id === item.id
        )
        if (sectionLineIndex > -1) {
          collection.sectionline_set.features.splice(sectionLineIndex, 1)
        }
      })
    },
    async uploadShapeFile(formData) {
      const sectionLinesStore = useSectionLinesStore()
      const projectStore = useProjectStore()
      const { currentItem: currentProject } = storeToRefs(projectStore)
      const uploadUrl = API.SECTION_GEO_DATA.SECTION_LINE_UPLOAD_COLLECTIONS.UPLOAD_TO_PROJECT(
        currentProject.value.id
      )
      const responseData = await api.apiService.api(uploadUrl, 'POST', formData)
      this.addItems([responseData])
      sectionLinesStore.addItems(responseData.sectionline_set.features)
      this.toggleAddSectionLineShapeFileDialog()
    },
    toggleCollection(onOff: boolean, collectionID) {
      const sectionLinesStore = useSectionLinesStore()
      if (onOff) {
        sectionLinesStore.selectSectionLinesByCollectionId(collectionID)
        this.selectedItems.push(this.items.find((item) => item.id === collectionID))
      } else {
        sectionLinesStore.deselectSectionLinesByCollectionId(collectionID)
        this.selectedItems = this.selectedItems.filter((item) => item.id !== collectionID)
      }
    }
  },
  getters: {
    ...baseStore.getters,
    selectedItemIDs: (state: State) => {
      return state.selectedItems.map((item) => item.id)
    },
    isMultipleSelection: (state: State) => {
      return state.selectedItems.length > 1
    },
    isSingleSelection: (state: State) => {
      return state.selectedItems.length === 1
    },
    sectionLinesByCollectionID: (state: State) => (collectionID: number) => {
      const collection = state.items.find((item) => item.id === collectionID)
      if (!collection) return []
      return collection.sectionline_set.features
    },
    isSelected: (state: State) => (collectionID) => {
      const sectionLinesStore = useSectionLinesStore()
      const collection = state.items.find((item) => item.id === collectionID)
      if (!collection) {
        return false
      }
      const sectionLines = collection?.sectionline_set?.features || []
      if (sectionLines.length === 0) {
        return state.selectedItems.some((item) => item.id === collectionID)
      }
      return sectionLines.every((sectionLine) => sectionLinesStore.isSelected(sectionLine.id))
    }
  }
})
